import React, { Component } from "react";
import AllGradients from "./AllGradients";
import * as d3 from "d3";
import * as R from "ramda";
import "../styles/components/map.css";

class PtsChart extends Component {
  constructor(props) {
    super(props);
    const datacenters = R.flatten(
      R.map(R.prop("dcs"), R.values(R.omit(["count"], this.props.datacenters)))
    );

    const links = R.map(
      (val) => ({
        source: this.findNode(val.first_datacenter.id, datacenters),
        target: this.findNode(val.second_datacenter.id, datacenters),
        connect_time: val.connect_time,
        connectivity_score: val.connectivity_score,
        max: 2,
      }),
      props.connections
    );

    //From http://stackoverflow.com/questions/11368339/drawing-multiple-edges-between-two-nodes-with-d3
    //Sort links by source, then target
    links.sort(function (a, b) {
      if (a.source > b.source) {
        return 1;
      } else if (a.source < b.source) {
        return -1;
      } else {
        if (a.target > b.target) {
          return 1;
        }
        if (a.target < b.target) {
          return -1;
        } else {
          return 0;
        }
      } //else
    }); //sort

    //Any links with duplicate source and target get an incremented 'linknum'
    for (var i = 0; i < links.length; i++) {
      if (
        i !== 0 &&
        links[i].source === links[i - 1].source &&
        links[i].target === links[i - 1].target
      ) {
        links[i].linknum = links[i - 1].linknum + 1;
      } else {
        links[i].linknum = 1;
      } //else
    } //for i

    this.state = {
      active: null,
      nodes: datacenters,
      links: links,
      width: 0,
      height: 0,
    };
  }

  createChart = () => {
    ///////////////////////////////////////////////////////////////////////////
    /////////////////////////// General variables /////////////////////////////
    ///////////////////////////////////////////////////////////////////////////
    var darkgrey = "#161616";
    var resizeSimilarityNetwork;

    var self = this;

    const statusColors = ["#ee5253", "#feca57", "#1dd1a1"];
    //Base scale for the font size
    var fontScale = d3.scaleLinear().domain([50, 300]).range([7, 15]);

    //The margin is scaled with the circle radius
    var marginScale = d3.scaleLinear().domain([150, 300]).range([30, 60]);

    ///////////////////////////////////////////////////////////////////////////
    ////////////////////////////// Set up the SVG /////////////////////////////
    ///////////////////////////////////////////////////////////////////////////

    var divWidth = parseInt(
      d3.select("#viz-similarity-network").style("width"),
      10
    );
    var windowHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight;

    //The radius of the network in circular shape
    var radius = Math.min((divWidth / 2) * 0.9, (windowHeight / 2) * 0.7, 300);

    var marginSize = Math.round(marginScale(radius));
    var margin = {
      top: marginSize,
      right: marginSize,
      bottom: marginSize,
      left: marginSize,
    };

    //Is the screen too small to use a circle shape?
    var circleShape = divWidth < 750 ? 0 : 1;

    var width = divWidth - margin.left - margin.right;
    var height = circleShape
      ? 2.6 * radius
      : windowHeight * 0.8 - margin.top - margin.bottom;

    //SVG container
    var svg = d3
      .select(this._rootNode)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr(
        "transform",
        "translate(" +
          (margin.left + width / 2) +
          "," +
          (margin.top + height / 2) +
          ")"
      )
      .style("font-size", round2(fontScale(radius)) + "px");

    ///////////////////////////////////////////////////////////////////////////
    ///////////////////// Figure out variables for layout /////////////////////
    ///////////////////////////////////////////////////////////////////////////

    var middleLang = undefined; //starting language in the middle
    var middleLangOld = undefined;
    var hoveredNode = undefined;
    //The fraction of the radius the links should expand outward
    var radiusNumOdd = [-0.3, 0.3, 0.5];
    var radiusNumEven = [-0.9, 0.2, 0.5];
    var radiusNumOddRect = [-0.4, 0.2, 0.5];
    var radiusNumEvenRect = [-2, 0, 0.5];

    //Scale for the white circles, one for each language
    var circleScale = d3.scaleLinear().domain([50, 300]).range([10, 45]);

    //Opacities for the links (without words)
    var linkOpacity = 0.15,
      linkOpacityEagleView = 0.3,
      middleLinkOpacity = 1,
      hoverLinkOpacity = 0.6;

    //Scale for the stroke width - based on the radius of the circle
    var strokeScale = d3.scaleLinear().domain([100, 250]).range([1, 3]);

    var rectLoc = calculateRectPositions();

    ///////////////////////////////////////////////////////////////////////////
    ////////////////////////// Create shadow filter ///////////////////////////
    ///////////////////////////////////////////////////////////////////////////

    //Filter for the outside glow
    var defs = svg.append("defs");
    var filter = defs.append("filter").attr("id", "shadow");

    filter
      .append("feColorMatrix")
      .attr("type", "matrix")
      .attr("values", "0 0 0 0   0 0 0 0 0   0 0 0 0 0   0 0 0 0 0.4 0");
    filter
      .append("feGaussianBlur")
      .attr("stdDeviation", "3")
      .attr("result", "coloredBlur");

    var feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    //Filter for the outside glow
    var filterG = defs.append("filter").attr("id", "glow");
    filterG
      .append("feGaussianBlur")
      .attr("stdDeviation", "3.5")
      .attr("result", "coloredBlur");
    var feMergeG = filterG.append("feMerge");
    feMergeG.append("feMergeNode").attr("in", "coloredBlur");
    feMergeG.append("feMergeNode").attr("in", "SourceGraphic");

    // Per-type markers, as they don't inherit styles.
    function marker(color) {
      defs
        .append("svg:marker")
        .attr("id", color.replace("#", ""))
        .attr("viewBox", "0 -5 10 10")
        .attr("refX", 15) // This sets how far back it sits, kinda
        .attr("refY", 0)
        .attr("markerWidth", 9)
        .attr("markerHeight", 9)
        .attr("orient", "auto")
        .attr("markerUnits", "userSpaceOnUse")
        .append("svg:path")
        .attr("d", "M0,-5L10,0L0,5")
        .style("fill", color);

      return "url(" + color + ")";
    }

    // svg
    //   .append("path")
    //   .attr("d", "M 10 80 Q 52.5 10, 95 80 T 180 80")
    //   // .attr("d", "M150,-259.81 A987.28,987.28 0 0,0 150,259.81 T 180 80")
    //   .style("stroke", "red")
    //   .style("fill", "none")
    //   .attr("marker-mid", marker("#ff0000"));
    ///////////////////////////////////////////////////////////////////////////
    ///////////////////////////// Final data prep /////////////////////////////
    ///////////////////////////////////////////////////////////////////////////
    var linkedByIndex = {};
    var nodes = this.state.nodes;
    var links = this.state.links;
    //Create nodes for each unique source and target.
    links.forEach(function (link, i) {
      link.index = i;
    });
    //For the hover effects
    //http://stackoverflow.com/questions/8739072/highlight-selected-node-its-links-and-its-children-in-a-d3-force-directed-grap
    links.forEach(function (d) {
      linkedByIndex[d.source.name + "," + d.target.name] = 1;
    });
    ///////////////////////////////////////////////////////////////////////////
    //////////////////////////// Draw the links /////////////////////////////
    ///////////////////////////////////////////////////////////////////////////

    function createLinks() {
      // Mark link for marker it would be invisible, and it will be used just for marker alignment
      var linkM = svg.selectAll(".linkM").data(links);

      linkM
        .select("path")
        .attr(
          "class",
          (d) => `link-pathM ${d.connectivity_score === 1 ? "" : "dashed"}`
        )
        .style("stroke", function (d) {
          return statusColors[d.connectivity_score - 1];
        })
        .style("stroke-width", function (d) {
          return 0;
        })
        .attr("marker-end", (d) =>
          marker(statusColors[d.connectivity_score - 1])
        );

      linkM
        .enter()
        .append("g")
        .attr("class", "linkM")
        .append("path")
        .attr(
          "class",
          (d) => `link-pathM ${d.connectivity_score === 1 ? "" : "dashed"}`
        )
        .style("stroke", function (d) {
          return statusColors[d.connectivity_score - 1];
        })
        .style("stroke-width", function (d) {
          return 0;
        })
        .style("opacity", function (d) {
          return middle(d)
            ? middleLinkOpacity
            : R.isNil(middleLang)
            ? linkOpacityEagleView
            : linkOpacity;
        })
        .attr("d", function (d) {
          return linkPathCalculationForMarker(d);
        })
        .attr("marker-end", (d) =>
          marker(statusColors[d.connectivity_score - 1])
        );

      linkM.exit().remove();
      // Normal links
      var link = svg.selectAll(".link").data(links);
      link.selectAll("text").remove();

      link
        .select("path")
        .attr(
          "class",
          (d) => `link-path ${d.connectivity_score === 1 ? "" : "dashed"}`
        )
        .style("stroke", function (d) {
          return statusColors[d.connectivity_score - 1];
        })
        .style("stroke-width", function (d) {
          return middle(d)
            ? +round2(strokeScale(radius))
            : +round2(strokeScale(radius)) * 0.75;
        })
        .style("filter", (d) =>
          d.connectivity_score === 1 ? "url(#glow)" : ""
        );

      link
        .enter()
        .append("g")
        .attr("class", "link")
        // .on("mouseover", linkMouseOver)
        // .on("mouseout", linkMouseOut);
        .append("path")
        // .attr("marker-mid", d => marker(statusColors[d.connectivity_score - 1]))
        .attr("id", function (d) {
          return "link-id-" + d.index;
        })
        .attr(
          "class",
          (d) => `link-path ${d.connectivity_score === 1 ? "" : "dashed"}`
        )
        .style("stroke", function (d) {
          return statusColors[d.connectivity_score - 1];
        })
        .style("stroke-width", function (d) {
          return middle(d)
            ? +round2(strokeScale(radius))
            : +round2(strokeScale(radius)) * 0.75;
        })
        .style("opacity", function (d) {
          return middle(d)
            ? middleLinkOpacity
            : R.isNil(middleLang)
            ? linkOpacityEagleView
            : linkOpacity;
        })
        .attr("d", function (d) {
          return linkPathCalculation(d);
        })
        .style("filter", (d) =>
          d.connectivity_score === 1 ? "url(#glow)" : ""
        );

      //Create a text element for the big bold part in the middle
      link
        .append("text")
        .attr("class", "link-text-bold noselect")
        .style("fill", "black")
        .attr("dy", "0.15em");

      //Create the text on the paths
      updateLinkTextPaths(hoveredNode);

      //Make middle links hoverable and the rest not
      link
        .filter(function (d) {
          return !middle(d);
        })
        .classed("link-no-hover", true);

      link.exit().remove();
    }

    this.createLinks = createLinks;
    ///////////////////////////////////////////////////////////////////////////
    //////////////////////////// Create the nodes /////////////////////////////
    ///////////////////////////////////////////////////////////////////////////
    function createNodes() {
      var node = svg
        .selectAll(".node")
        .data(nodes)
        .enter()
        .append("g")
        .attr("class", "node")
        // .attr("fill", d => `url(#pink)`)
        .attr("transform", function (d) {
          return "translate(" + d.x + "," + d.y + ")";
        })
        .on("click", switchCenterLanguage)
        .on("mouseover", nodeMouseOver)
        .on("mouseout", nodeMouseOut);

      //Append a white circle for each language
      node
        .append("circle")
        .attr("class", "node-background-circle")
        .attr("r", circleScale(radius * 1.3))
        .style("fill", "#21252E")
        .style("filter", "url(#shadow)");

      //Append the language text in the circle
      node
        .append("text")
        .attr("class", "node-language-text")
        .attr("dy", "0.35em")
        .text(function (d) {
          return d.name;
        });

      const countryFlagWidth = 50;
      const countryFlagRadius = countryFlagWidth / 2;
      const defs = svg.append("defs");
      defs
        .append("clipPath")
        .attr("id", "avatar-clip")
        .append("circle")
        .attr("cx", countryFlagRadius)
        .attr("cy", countryFlagRadius)
        .attr("r", countryFlagRadius / 2);

      d3.selectAll(".node")
        .insert("image", "text")
        .attr("x", 0)
        .attr("y", 0)
        .attr("class", "country-code")
        .attr(
          "xlink:href",
          (d) => `/images/png100px/${d.country_code.toLowerCase()}.png`
        )
        .attr("width", countryFlagWidth)
        .attr("height", countryFlagWidth)
        .attr("clip-path", "url(#avatar-clip)")
        .attr(
          "transform",
          `translate(${circleScale(radius * 1.3) * 0.3}, ${
            -circleScale(radius * 1.3) * 1.1
          })`
        )
        .style("filter", "url(#shadow)");
    }

    ////////////////////////////////////////////////////////////////////////////
    ///////////////////////////// Get data & Draw Map /////////////////////////
    ///////////////////////////////////////////////////////////////////////////
    function drawMap() {
      //Set up the locations of the nodes
      calculateNodePositions(nodes);
      createLinks();
      createNodes();
    }
    drawMap();

    this.drawMap = drawMap;

    ///////////////////////////////////////////////////////////////////////////
    ///////////////////////////// Switch languages ////////////////////////////
    ///////////////////////////////////////////////////////////////////////////

    function switchCenterLanguage(d) {
      //Do nothing if the middle one was clicked
      function intermediateSwitch() {
        //Switch locations of the middle and clicked on node
        nodes.forEach(function (n, i) {
          if (n.name === middleLangOld) {
            n.xOld = 0;
            n.yOld = 0;
            n.angleOld = 0;
            n.x = x;
            n.y = y;
            n.angle = angle;
          } else if (n.name === d.name) {
            n.xOld = x;
            n.yOld = y;
            n.angleOld = angle;
            n.x = 0;
            n.y = 0;
            n.angle = 0;
          } else {
            n.xOld = n.x;
            n.yOld = n.y;
            n.angleOld = n.angle;
          }
        }); //forEach

        links.forEach(function (l, i) {
          l.source = nodes.find((n) => n.id === l.source.id);
          l.target = nodes.find((n) => n.id === l.target.id);
        });
        calculateNodePositions(nodes);

        //Switch locations of the center and clicked node
        svg
          .selectAll(".node")
          .transition("change")
          .duration(500)
          .attr("transform", function (d) {
            return "translate(" + d.x + "," + d.y + ")";
          })
          .transition()
          .duration(0)
          .call(endall, fadeIn);

        //Update the paths
        svg
          .selectAll(".link-path")
          //Update the hidden paths - before the move - so the transition will look smooth
          // .filter(function(d) {
          //   //Only change the paths that move
          //   return middle(d) || middleOld(d);
          // })
          .attr("d", function (d) {
            return linkPathCalculationSwitch(d);
          })
          .transition("reform")
          .duration(500)
          //.attr("d", linkPathCalculation)
          .attrTween("d", function (n) {
            //https://bl.ocks.org/mbostock/3916621
            var d1 = linkPathCalculation(n),
              precision = 10;

            var path0 = this,
              path1 = path0.cloneNode(),
              n0 = path0.getTotalLength(),
              n1 = (path1.setAttribute("d", d1), path1).getTotalLength();

            // Uniform sampling of distance based on specified precision.
            var distances = [0],
              i = 0,
              dt = precision / Math.max(n0, n1);
            while ((i += dt) < 1) distances.push(i);
            distances.push(1);

            // Compute point-interpolators at each distance.
            var points = distances.map(function (t) {
              var p0 = path0.getPointAtLength(t * n0),
                p1 = path1.getPointAtLength(t * n1);
              return d3.interpolate([p0.x, p0.y], [p1.x, p1.y]);
            });

            return function (t) {
              return t < 1
                ? "M" +
                    points
                      .map(function (p) {
                        return p(t);
                      })
                      .join("L")
                : d1;
            };
          });

        svg
          .selectAll(".link-pathM")
          //Update the hidden paths - before the move - so the transition will look smooth
          // .filter(function(d) {
          //   //Only change the paths that move
          //   return middle(d) || middleOld(d);
          // })
          .attr("d", function (d) {
            return linkPathCalculationSwitchForMarker(d);
          })
          .transition("reform")
          .duration(500)
          //.attr("d", linkPathCalculation)
          .attrTween("d", function (n) {
            //https://bl.ocks.org/mbostock/3916621
            var d1 = linkPathCalculationForMarker(n),
              precision = 10;

            var path0 = this,
              path1 = path0.cloneNode(),
              n0 = path0.getTotalLength(),
              n1 = (path1.setAttribute("d", d1), path1).getTotalLength();

            // Uniform sampling of distance based on specified precision.
            var distances = [0],
              i = 0,
              dt = precision / Math.max(n0, n1);
            while ((i += dt) < 1) distances.push(i);
            distances.push(1);

            // Compute point-interpolators at each distance.
            var points = distances.map(function (t) {
              var p0 = path0.getPointAtLength(t * n0),
                p1 = path1.getPointAtLength(t * n1);
              return d3.interpolate([p0.x, p0.y], [p1.x, p1.y]);
            });

            return function (t) {
              return t < 1
                ? "M" +
                    points
                      .map(function (p) {
                        return p(t);
                      })
                      .join("L")
                : d1;
            };
          });

        svg
          .selectAll(".link-path")
          .filter(function (d) {
            return middle(d) || middleOld(d);
          })
          .transition("changeColor")
          .duration(500)
          .style("opacity", function (d) {
            return middle(d)
              ? middleLinkOpacity
              : R.isNil(middleLang)
              ? linkOpacityEagleView
              : linkOpacity;
          })
          .style("filter", (d) =>
            d.connectivity_score === 1 ? "url(#glow)" : ""
          )
          .call(endall, updateLinkTextPaths); //Adjust the text paths'
        svg
          .selectAll(".link-pathM")
          .style("opacity", 0)
          .filter(function (d) {
            return middle(d) || middleOld(d);
          })
          .transition("changeColor")
          .duration(500)
          .style("opacity", function (d) {
            return middle(d)
              ? middleLinkOpacity
              : R.isNil(middleLang)
              ? linkOpacityEagleView
              : linkOpacity;
          })
          .call(endall, updateLinkTextPaths); //Adjust the text paths'
      } //function intermediateSwitch

      //Basically do a mouseout but then more slowly
      function fadeIn() {
        //Fade everything back in
        svg
          .selectAll(".node, .link")
          .transition("fade")
          .duration(500)
          .style("opacity", 1);
        svg
          .selectAll(".node, .linkM")
          .transition("fade")
          .duration(500)
          .style("opacity", 1);
        svg
          .selectAll(".link-path")
          //.transition("fadeIn").duration(500)
          //.style("opacity", function(d) { return middle(d) ? middleLinkOpacity : linkOpacity; })
          .style("stroke-width", function (d) {
            return middle(d)
              ? +round2(strokeScale(radius))
              : +round2(strokeScale(radius)) * 0.75;
          })
          .style("filter", (d) =>
            d.connectivity_score === 1 ? "url(#glow)" : ""
          );

        svg
          .selectAll(".link-pathM")
          //.transition("fadeIn").duration(500)
          //.style("opacity", function(d) { return middle(d) ? middleLinkOpacity : linkOpacity; })
          .style("stroke-width", 0);
        //Set the hovers back on
        svg
          .selectAll(".node")
          .on("click", switchCenterLanguage)
          .on("mouseover", nodeMouseOver)
          .on("mouseout", nodeMouseOut);
        //Make middle links hoverable and the rest not
        svg.selectAll(".link").classed("link-no-hover", function (d) {
          return !middle(d);
        });
      } //function fadeIn

      if (d.name === middleLang) {
        middleLang = undefined;
        middleLangOld = undefined;
        calculateNodePositions(nodes);
        intermediateSwitch();
        return;
      } else {
        var x = d.x,
          y = d.y,
          angle = d.angle;

        //Remeber the before middle node's language
        middleLangOld = middleLang;
        //Change id for middle language
        middleLang = d.name;

        //Deactivate the mouse events
        svg
          .selectAll(".node")
          .on("click", null)
          .on("mouseover", null)
          .on("mouseout", null);

        //Remove hover class from all links, i.e. deactivate mouse events on links
        svg.selectAll(".link").classed("link-no-hover", true);

        //Fade out the textPaths and then remove them
        svg
          .selectAll(".link")
          .selectAll("textPath")
          .transition("fade")
          .duration(200)
          .style("opacity", 0)
          .call(endall, intermediateSwitch)
          .remove();
      } //else
    } //function switchCenterLanguage

    ///////////////////////////////////////////////////////////////////////////
    ///////////////////////// Do all the textPath things //////////////////////
    ///////////////////////////////////////////////////////////////////////////

    function updateLinkTextPaths(dest = undefined) {
      var svg = d3.selectAll("#viz-similarity-network svg");

      //Remove all the text paths, because we need to recalculate the text lengths
      svg.selectAll("textPath").remove();

      //Add the bold middle connect_time back in
      svg
        .selectAll(".link-text-bold")
        .filter(function (d) {
          return middle(d) || connectedLinks(d, dest);
        })
        .each(function (d, i) {
          var el = d3.select(this);

          var startOff = d.st === "stn" ? "60%" : "40%";
          if (d.target.name === middleLang)
            startOff = d.st === "stn" ? "40%" : "60%";
          //If the lines are too short in the rectangular version, place them in the middle
          if (!circleShape) {
            if (d.target.name === middleLang && d.source.y === 0)
              startOff = "50%";
            else if (d.source.name === middleLang && d.target.y === 0)
              startOff = "50%";
          } //if

          el.append("textPath")
            .style("color", darkgrey)
            .style(
              "text-shadow",
              "0px 1px 0px #15181E, 1px 0px 0px #15181E, 0px -1px 0px #15181E, -1px 0px 0px #15181E"
            )
            .attr("class", "link-text-middle")
            .attr("startOffset", startOff)
            .style("text-anchor", "middle")
            .style("opacity", 0)
            .style("fill", "#f6f6f6")
            .attr("xlink:href", "#link-id-" + d.index)
            .text(d.connect_time);
        });

      //Now show them
      svg
        .selectAll("textPath")
        // .transition("fade")
        // .duration(250)
        .style("opacity", 1);
    } //function updateLinkTextPaths

    ///////////////////////////////////////////////////////////////////////////
    /////////////////////////// Calculate the paths ///////////////////////////
    ///////////////////////////////////////////////////////////////////////////

    //Calculate the path shape of the links
    function linkPathCalculation(d) {
      var dx = d.target.x - d.source.x,
        dy = d.target.y - d.source.y,
        dr = Math.sqrt(dx * dx + dy * dy);

      d.sweepflag = d.linknum % 2 === 0 ? 1 : 0;
      d.st = "stn"; //Normal from source to target line

      var radiusNOdd = radiusNumOddRect;
      var radiusNEven = radiusNumEvenRect;
      //For the paths on the y=0 line, use the circular distances (which are a bit more)
      if (
        circleShape ||
        (!circleShape && d.source.y === 0 && d.target.y === 0)
      ) {
        radiusNOdd = radiusNumOdd;
        radiusNEven = radiusNumEven;
      } //if

      //Figure out the best radius to give the arc
      if (d.max % 2 === 1) {
        //Is the total number of links uneven
        if (d.linknum === 1) {
          //For the first link
          //If there are multiple links to draw between these nodes, make the first straight
          if (d.max !== 1) {
            d.arc = 0;
          } else {
            //If there is only 1 link, make it curved
            //For the line towards the center, just make it all clockwise
            if (d.source.name === middleLang) {
              d.sweepflag = 1;
            } else if (d.target.name === middleLang) {
              d.sweepflag = 0;
            } else {
              //For the rest, figure out in which quadrant it lies wrt the source
              var da = (d.target.angle - d.source.angle) / Math.PI;
              if ((da >= 0 && da < 1) || (da >= -2 && da < -1)) {
                d.sweepflag = 1;
              } //if
            } //else
            d.arc = dr;
          } //else
        } else {
          //for the links greater than number 1
          d.arc = dr - radiusNOdd[Math.floor(d.linknum / 2) - 1] * dr;
        } //else
      } else {
        //for the even numbered total links
        d.arc = dr - radiusNEven[Math.ceil(d.linknum / 2) - 1] * dr;
      } //else

      var x1 = round2(d.source.x),
        y1 = round2(d.source.y),
        x2 = round2(d.target.x),
        y2 = round2(d.target.y);

      //Make sure the middle paths, on which text will be written will be sort of upside
      if (middle(d)) {
        if (d.source.name === middleLang && d.target.x < 0) {
          //middlelang is the source & target is on the left of the middle node
          d.sweepflag = d.sweepflag ? 0 : 1;
          d.st = "str"; //Reversed: from target to source line
          x1 = round2(d.target.x);
          y1 = round2(d.target.y);
          x2 = round2(d.source.x);
          y2 = round2(d.source.y);
        } else if (d.target.name === middleLang && d.source.x > 0) {
          //middleLang is the target & source is on the right of the middle node
          d.sweepflag = d.sweepflag ? 0 : 1;
          d.st = "str"; //Reversed: from target to source line
          x1 = round2(d.target.x);
          y1 = round2(d.target.y);
          x2 = round2(d.source.x);
          y2 = round2(d.source.y);
        } //else if
      } //if
      else {
        if (d.target.x - d.source.x < 0) {
          //middlelang is the source & target is on the left of the middle node
          d.sweepflag = d.sweepflag ? 0 : 1;
          d.st = "str"; //Reversed: from target to source line
          x1 = round2(d.target.x);
          y1 = round2(d.target.y);
          x2 = round2(d.source.x);
          y2 = round2(d.source.y);
        }
      }

      var path =
        "M" +
        x1 +
        "," +
        y1 +
        " A" +
        dr +
        "," +
        dr +
        " 0 0," +
        d.sweepflag +
        " " +
        x2 +
        "," +
        y2;
      return path;
    } //function linkPathCalculation

    function linkPathCalculationForMarker(d) {
      d.sweepflag = d.linknum % 2 === 0 ? 1 : 0;

      var dx = d.target.x - d.source.x,
        dy = d.target.y - d.source.y,
        dr = Math.sqrt(dx * dx + dy * dy);

      // We know the center of the arc will be some distance perpendicular from the
      // link segment's midpoint. The midpoint is computed as:
      var endX = (d.target.x + d.source.x) / 2;
      var endY = (d.target.y + d.source.y) / 2;

      // Notice that the paths are the arcs generated by a circle whose
      // radius is the same as the distance between the nodes. This simplifies the
      // trig as we can simply apply the 30-60-90 triangle rule to find the difference
      // between the radius and the distance to the segment midpoint from the circle
      // center.
      var len = dr - (dr / 2) * Math.sqrt(3);

      // Remember that is we have a line's slope then the perpendicular slope is the
      // negative inverse.
      endX = endX + (-dy * len) / dr;
      endY = endY + (dx * len) / dr;

      console.log();

      return (
        "M" +
        d.source.x +
        "," +
        d.source.y +
        "A" +
        dr +
        "," +
        dr +
        " 0 0," +
        d.sweepflag +
        endX +
        "," +
        endY
      );
    } //function linkPathCalculation

    //Intermediate link path calculation for when the center node switches
    function linkPathCalculationSwitch(d) {
      var sweepflag;
      var st = "stn";

      var x1 = round2(d.source.xOld),
        y1 = round2(d.source.yOld),
        x2 = round2(d.target.xOld),
        y2 = round2(d.target.yOld);

      //Make sure the middle paths, on which text will be written will be sort of upside
      if (middle(d)) {
        if (d.source.name === middleLang && d.target.x < 0) {
          //middlelang is the source & target is on the left of the middle node
          st = "str";
          x1 = round2(d.target.xOld);
          y1 = round2(d.target.yOld);
          x2 = round2(d.source.xOld);
          y2 = round2(d.source.yOld);
        } else if (d.target.name === middleLang && d.source.x > 0) {
          //middleLang is the target & source is on the right of the middle node
          st = "str";
          x1 = round2(d.target.xOld);
          y1 = round2(d.target.yOld);
          x2 = round2(d.source.xOld);
          y2 = round2(d.source.yOld);
        } //else if
      } //if

      //Rules to figure out what the sweepflag should be
      if (d.st === "stn" && st === "stn") sweepflag = d.sweepflag;
      if ((d.st === "str" && st === "stn") || (d.st === "stn" && st === "str"))
        sweepflag = d.sweepflag ? 0 : 1;
      if (d.st === "str" && st === "str") {
        sweepflag = d.sweepflag ? 1 : 0;
        // console.log("from str to str");
      }

      // console.log(x1, y1, round2(d.arc), sweepflag, x2, y2);

      var path =
        "M" +
        x1 +
        "," +
        y1 +
        " A" +
        round2(d.arc) +
        "," +
        round2(d.arc) +
        " 0 0," +
        sweepflag +
        " " +
        x2 +
        "," +
        y2;
      return path;
    } //function linkPathCalculationSwitch

    function linkPathCalculationSwitchForMarker(d) {
      var sweepflag;
      var st = "stn";

      var x1 = round2(d.source.xOld),
        y1 = round2(d.source.yOld),
        x2 = round2(d.target.xOld),
        y2 = round2(d.target.yOld);

      //Rules to figure out what the sweepflag should be
      if (d.st === "stn" && st === "stn") sweepflag = d.sweepflag;
      if ((d.st === "str" && st === "stn") || (d.st === "stn" && st === "str"))
        sweepflag = d.sweepflag ? 0 : 1;
      if (d.st === "str" && st === "str") {
        sweepflag = d.sweepflag ? 0 : 1;
        // console.log("from str to str");
      }

      var path =
        "M" +
        x1 +
        "," +
        y1 +
        " A" +
        round2(d.arc) +
        "," +
        round2(d.arc) +
        " 0 0," +
        sweepflag +
        " " +
        x2 +
        "," +
        y2;
      return path;
    } //function linkPathCalculationSwitch

    ///////////////////////////////////////////////////////////////////////////
    //////////////////////// Resize the path on an update /////////////////////
    ///////////////////////////////////////////////////////////////////////////

    resizeSimilarityNetwork = function () {
      // console.log("resize similarity network");

      // ------------------------ Update the SVGs --------------------------- //

      divWidth = parseInt(
        d3.select("#viz-similarity-network").style("width"),
        10
      );
      windowHeight =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        document.body.clientHeight;

      var radius = Math.min(
        (divWidth / 2) * 0.9,
        (windowHeight / 2) * 0.7,
        300
      );

      marginSize = Math.round(marginScale(radius));
      margin = {
        top: marginSize,
        right: marginSize,
        bottom: marginSize,
        left: marginSize,
      };

      circleShape = divWidth < 750 ? 0 : 1;
      width = divWidth - margin.left - margin.right;
      height = circleShape
        ? 2.6 * radius
        : windowHeight * 0.8 - margin.top - margin.bottom;

      //Adjust SVG container
      d3.select(self._rootNode)
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom);
      svg = d3.select("#viz-similarity-network svg g");

      //Adjust the center and font size
      svg
        .attr(
          "transform",
          "translate(" +
            (margin.left + width / 2) +
            "," +
            (margin.top + height / 2) +
            ")"
        )
        .style("font-size", round2(fontScale(radius)) + "px");

      // --------------------- Update locations and sizes ---------------------- //

      //Put nodes on new locations
      rectLoc = calculateRectPositions();
      calculateNodePositions(this.state.nodes);
      svg.selectAll(".node").attr("transform", function (d) {
        return "translate(" + d.x + "," + d.y + ")";
      });

      //Resize background circle
      svg
        .selectAll(".node-background-circle")
        .attr("r", circleScale(radius * 1.3));

      //Update the paths between the circles
      svg
        .selectAll(".link-path")
        .style("stroke-width", function (d) {
          return middle(d)
            ? +round2(strokeScale(radius))
            : +round2(strokeScale(radius)) * 0.75;
        })
        .attr("d", function (d) {
          return linkPathCalculation(d);
        })
        .style("filter", (d) =>
          d.connectivity_score === 1 ? "url(#glow)" : ""
        );

      svg.selectAll(".link-pathM").attr("d", function (d) {
        return linkPathCalculationForMarker(d);
      });

      //Update the text paths
      updateLinkTextPaths();
    }; //function resizeSimilarityNetwork\
    this.resizeSimilarityNetwork = resizeSimilarityNetwork;

    ///////////////////////////////////////////////////////////////////////////
    ///////////////////////////// Extra functions /////////////////////////////
    ///////////////////////////////////////////////////////////////////////////

    //Mouse over a language circle
    function nodeMouseOver(d) {
      var opacity = 0.1;
      hoveredNode = d;
      //Dim all other nodes
      svg
        .selectAll(".node")
        .transition()
        .duration(0)
        .style("opacity", function (o) {
          return neighboring(d, o) || d === o ? 1 : opacity;
        });
      //Dim unconnected links
      svg
        .selectAll(".link")
        .transition()
        .duration(0)
        .style("opacity", function (o) {
          return o.source === d || o.target === d ? 1 : opacity;
        });
      svg
        .selectAll(".linkM")
        .transition()
        .duration(0)
        .style("opacity", function (o) {
          return o.source === d || o.target === d ? 1 : opacity;
        });
      svg
        .selectAll(".link-path")
        .transition()
        .duration(0)
        .style("opacity", function (o) {
          if (middle(o)) {
            return middleLinkOpacity;
          } else {
            return o.source === d || o.target === d
              ? hoverLinkOpacity
              : opacity;
          } //else
        })
        .call(() => {
          updateLinkTextPaths(d);
        });

      svg
        .selectAll(".link-pathM")
        .transition()
        .duration(0)
        .style("opacity", function (o) {
          if (middle(o)) {
            return middleLinkOpacity;
          } else {
            return o.source === d || o.target === d
              ? hoverLinkOpacity
              : opacity;
          } //else
        });
    } //nodeMouseOver

    //Mouse out a language circle
    function nodeMouseOut(d) {
      hoveredNode = undefined;
      //Bring everything back to normal
      svg.selectAll(".node").style("opacity", 1);
      svg.selectAll(".link").style("opacity", 1);
      svg.selectAll(".linkM").style("opacity", 1);
      svg
        .selectAll(".link")
        .selectAll(".link-path")
        .style("opacity", function (d) {
          return middle(d)
            ? middleLinkOpacity
            : R.isNil(middleLang)
            ? linkOpacityEagleView
            : linkOpacity;
        })
        .style("filter", (d) =>
          d.connectivity_score === 1 ? "url(#glow)" : ""
        )
        .call(updateLinkTextPaths);

      svg
        .selectAll(".linkM")
        .selectAll(".link-pathM")
        .style("opacity", function (d) {
          return middle(d)
            ? middleLinkOpacity
            : R.isNil(middleLang)
            ? linkOpacityEagleView
            : linkOpacity;
        });
    } //nodeMouseOut

    //Calculate the positions of the language circles
    function calculateNodePositions(nodes) {
      var counter = 0;
      nodes.forEach(function (d) {
        if (d.name === middleLang) {
          d.xOld = 0;
          d.yOld = 0;
          d.angleOld = 0;
          d.x = 0;
          d.y = 0;
          d.angle = 0;
        } else {
          if (R.isNil(middleLang)) {
            d.angle = (counter * 2 * Math.PI) / nodes.length;
          } else {
            d.angle = (counter * 2 * Math.PI) / (nodes.length - 1);
          }

          if (circleShape) {
            //Circle shape
            d.x = +round2(radius * Math.cos(d.angle));
            d.y = +round2(radius * Math.sin(d.angle));
          } else {
            //Rectangle shape
            d.x = rectLoc[counter].x;
            d.y = rectLoc[counter].y;
          } //else
          d.xOld = d.x;
          d.yOld = d.y;
          d.angleOld = d.angle;
          counter += 1;
        } //else
      }); //forEach nodes
    } //calculateNodePositions

    //Locations for a rectangular shape for small screens
    function calculateRectPositions() {
      var of = 7 / 24;
      var rectLoc = [
        {
          x: width / 2,
          y: 0,
        },
        {
          x: width / 2,
          y: height * of,
        },
        {
          x: width / 4,
          y: height / 2,
        },
        {
          x: -width / 4,
          y: height / 2,
        },
        {
          x: -width / 2,
          y: height * of,
        },
        {
          x: -width / 2,
          y: 0,
        },
        {
          x: -width / 2,
          y: -height * of,
        },
        {
          x: -width / 4,
          y: -height / 2,
        },
        {
          x: width / 4,
          y: -height / 2,
        },
        {
          x: width / 2,
          y: -height * of,
        },
      ];
      return rectLoc;
    } //function calculateRectPositions

    function neighboring(a, b) {
      return (
        linkedByIndex[a.name + "," + b.name] ||
        linkedByIndex[b.name + "," + a.name]
      );
    } //function neighboring

    //Is the link attached to the middle circle
    function middle(d) {
      return R.isNil(middleLang)
        ? false
        : d.source.name === middleLang || d.target.name === middleLang;
    } //function middle
    function connectedLinks(d, hNode) {
      return R.isNil(hNode)
        ? false
        : d.source.id === hNode.id || d.target.id === hNode.id;
    } //function middle
    function middleOld(d) {
      return d.source.name === middleLangOld || d.target.name === middleLangOld;
    } //function middleOld

    //Round number to 2 behind the decimal
    function round2(num) {
      return Math.round((num + 0.00001) * 100) / 100;
    } //round2

    //Endall function for d3v5
    function endall(transition, callback) {
      if (typeof callback !== "function")
        throw new Error("Wrong callback in endall");
      if (transition.size() === 0) {
        callback();
      }
      var n = 0;
      transition
        .each(function () {
          ++n;
        })
        .on("end", function () {
          if (!--n) callback.apply(this, arguments);
        });
    } //endall};
  };

  updateDimensions = () => {
    this.setState(
      () => ({ width: window.innerWidth, height: window.innerHeight }),
      this.resizeSimilarityNetwork
    );
  };

  // Create chart on mount
  componentDidMount() {
    this.createChart();
    window.addEventListener("resize", this.updateDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateDimensions);
  }

  UpdateNodes = (prevProps, newProps) => {
    var oldData = R.clone(prevProps);
    var newData = R.clone(newProps);
    var oldNodes = R.flatten(
      R.map(R.prop("dcs"), R.values(R.omit(["count"], oldData.datacenters)))
    );
    var newNodes = R.flatten(
      R.map(R.prop("dcs"), R.values(R.omit(["count"], newData.datacenters)))
    );
    var NDiff = R.symmetricDifferenceWith(
      (x, y) => x.id === y.id,
      oldNodes,
      newNodes
    );
    if (!R.isEmpty(NDiff)) {
      const byType = R.groupBy(function (obj) {
        var finder = R.any((el) => el.id === obj.id);
        var lhs = finder(oldNodes);
        var rhs = finder(newNodes);

        var onlyLeft = lhs && !rhs;
        var onlyRight = rhs && !lhs;
        var both = lhs && rhs;

        return onlyLeft ? "Delete" : onlyRight ? "Add" : both ? "Edit" : "None";
      });
      NDiff = byType(NDiff);
      return NDiff;
    }
    return null;
  };

  findNode = (id, nodes) => {
    for (var i in nodes) {
      if (nodes[i]["id"] === id) return nodes[i];
    }
  };

  UpdateLinks = (prevProps, newProps) => {
    var oldData = R.clone(prevProps).connections;
    var newData = R.clone(newProps).connections;
    // console.log(oldData, newData);
    var LDiff = R.symmetricDifferenceWith(
      (x, y) =>
        x.first_datacenter.id === y.first_datacenter.id &&
        x.second_datacenter.id === y.second_datacenter.id,
      oldData,
      newData
    );
    var LMDiff = R.differenceWith(
      (x, y) =>
        x.first_datacenter.id === y.first_datacenter.id &&
        x.second_datacenter.id === y.second_datacenter.id &&
        x.connect_time === y.connect_time,
      newData,
      oldData
    );
    if (!R.isEmpty(LDiff) || !R.isEmpty(LMDiff)) {
      const byType = R.groupBy(function (obj) {
        var finder = R.any(
          (el) => el.source === obj.source && el.target === obj.target
        );
        var lhs = finder(oldData);
        var rhs = finder(newData);

        var onlyLeft = lhs && !rhs;
        var onlyRight = rhs && !lhs;
        var both = lhs && rhs;

        return onlyLeft ? "Delete" : onlyRight ? "Add" : both ? "Edit" : "None";
      });
      LDiff = byType(LDiff);
      LDiff.Edit = LMDiff;
      return LDiff;
    }
    return null;
  };

  // When data is updated, re-render by playing animate() once
  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    var NDiff = this.UpdateNodes(prevProps, this.props);
    var LDiff = this.UpdateLinks(prevProps, this.props);
    if (!R.isNil(NDiff) || !R.isNil(LDiff)) {
      this.setState(
        (prevState) => {
          var nodes = prevState.nodes;
          if (!R.isNil(NDiff)) {
            if (!R.isNil(NDiff.Delete)) {
              nodes = nodes.filter((el) => {
                var delList = R.map(R.prop("id"), NDiff.Delete);
                return !delList.includes(el.id);
              });
            }
            if (!R.isNil(NDiff.Add)) {
              nodes = [...nodes, ...NDiff.Add];
            }
          }
          // ------------------------- //
          var links = prevState.links;
          if (!R.isNil(LDiff)) {
            if (!R.isEmpty(LDiff.Edit)) {
              LDiff.Edit.forEach((el) => {
                links.find(
                  (l) =>
                    el.first_datacenter.id === l.source.id &&
                    el.second_datacenter.id === l.target.id
                ).connect_time = el.connect_time;
                links.find(
                  (l) =>
                    el.first_datacenter.id === l.source.id &&
                    el.second_datacenter.id === l.target.id
                ).connectivity_score = el.connectivity_score;
              });
            }
          }
          // ------------------------- //
          return {
            nodes,
            links,
          };
        },
        () => {
          this.createLinks();
        }
      );
    }
  }

  _setRef(componentNode) {
    this._rootNode = componentNode;
  }

  render() {
    return (
      <div
        className="pts-chart"
        id="viz-similarity-network"
        style={this.props.style}
      >
        <svg ref={this._setRef.bind(this)}>
          <AllGradients />
        </svg>
      </div>
    );
  }
}

export default PtsChart;
